import * as React from 'react';

import { AutocompleteProps } from '@lightspeed/flame/Autocomplete';
import { Tooltip } from '@lightspeed/flame/Tooltip';
import debounce from 'lodash/debounce';
import uniqBy from 'lodash/uniqBy';

import { createReactRenderer, getReactNodes } from 'helpers/react-renderer';

import Autocomplete from '..';
import GuiDefHiddenInput from '../GuiDefHiddenInput';
import { createStringValueFromArray, searchTags } from './utils';

const loadSuggestions = (query: string, callback: (args?: any) => any) => {
  if (!query.includes(',')) {
    searchTags(query).then((resp) => callback(resp));
  } else {
    callback();
  }
};

const debouncedLoadSuggestions = debounce(loadSuggestions, 300);

let copyLabelTimeout: NodeJS.Timeout;

const TagsAutocomplete: React.FC<AutocompleteProps> = ({
  isClearable = true,
  isAsync = true,
  defaultValue = [],
  placeholder = 'Tag(s)',
  hiddenInput,
  title,
  onChange: onChangeProp,
  ...rest
}) => {
  const [value, setValue] = React.useState<AutocompleteOption[]>(defaultValue);
  const [query, setQuery] = React.useState('');
  const [copiedLabel, setCopiedLabel] = React.useState('');

  const updateValues = React.useCallback(() => {
    setValue((oldValue) => {
      const newValue = uniqBy(
        [
          ...oldValue,
          ...query
            .split(',')
            .filter(Boolean)
            .map((rawValue) => ({
              value: rawValue,
              label: rawValue,
            })),
        ],
        ({ value }) => `${value}`.toLowerCase()
      );
      onChangeProp?.(newValue);
      return newValue;
    });
    setQuery('');
  }, [query, onChangeProp]);

  React.useEffect(() => {
    if (query.includes(',')) {
      updateValues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return (
    <>
      {title ? <label>{title}</label> : null}
      <Autocomplete
        aria-label="tags-autocomplete-input"
        {...rest}
        value={value}
        inputValue={query.trimStart()}
        loadOptions={debouncedLoadSuggestions}
        placeholder={!rest.isDisabled && placeholder}
        isMulti
        isClearable={isClearable}
        menuIsOpen={!!query}
        isAsync={isAsync}
        onChange={(newValue: AutocompleteOption[]) => {
          setValue(newValue);
          onChangeProp?.(newValue);
        }}
        onInputChange={(query: string) => setQuery(query)}
        onKeyDown={(e: KeyboardEvent) => {
          // Hack: quickly writes the search query into the hidden input if the "Enter"
          // key is pressed and there are no results yet so the form submit gets the correct value
          if (e.key === 'Enter' && !document.querySelectorAll('.cr-autocomplete__option').length) {
            updateValues();
          }
        }}
        components={{
          DropdownIndicator: () => null,
          MultiValueLabel: (props: any) => {
            return copiedLabel === props.data.label ? (
              <Tooltip active={true} content="Copied">
                <var {...props.innerProps}>{props.data.label}</var>
              </Tooltip>
            ) : (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
              <var
                {...props.innerProps}
                onClick={() => {
                  setCopiedLabel(props.data.label);
                  navigator.clipboard.writeText(props.data.label).then(() => {
                    clearTimeout(copyLabelTimeout);
                    copyLabelTimeout = setTimeout(() => {
                      setCopiedLabel('');
                    }, 1000);
                  });
                }}
              >
                {props.data.label}
              </var>
            );
          },
        }}
      />

      {hiddenInput ? (
        // @ts-ignore
        <GuiDefHiddenInput {...hiddenInput} defaultValue={createStringValueFromArray(value)} />
      ) : null}
    </>
  );
};

const RenderedComponent = createReactRenderer({
  component: TagsAutocomplete,
  selector: '[data-react-root="TagsAutocomplete"]',
});

export default TagsAutocomplete;

history.onChangeState(
  [
    {
      name: [
        'item.views.item',
        'item.views.register_item',
        'item.views.quick_add',
        'item.views.register_workorder_item',
        'item_group.views.item_group',
        'customer.views.customer',
      ],
      form_name: 'view',
      id: undefined,
    },
    { form_name: 'listing', name: undefined },
  ],
  ({ form_name: formName, tab }) => {
    if (formName === 'view' && (tab === 'details' || tab === 'setup')) {
      getReactNodes('[data-react-root="TagsAutocomplete"]').forEach((node) => {
        if (node.offsetWidth) {
          node.parentElement!.style.width = `${node.offsetWidth / 2}px`;
        }
      });
    }

    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
